/**
 * DnD de arquivos sobre a janela de chat.
 * SRP: só contador de drag + handlers; staging fica em useChatAttachFlow.
 */
import { useCallback, useRef, useState, type DragEvent } from 'react';
import {
  dataTransferHasFiles,
  extractDataTransferFiles,
} from '../lib/attachSession';

interface UseChatFileDropOptions {
  enabled: boolean;
  onFiles: (files: File[]) => void;
}

export function useChatFileDrop({ enabled, onFiles }: UseChatFileDropOptions) {
  const [isDragging, setIsDragging] = useState(false);
  const depthRef = useRef(0);
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  const reset = useCallback(() => {
    depthRef.current = 0;
    setIsDragging(false);
  }, []);

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      if (!enabled || !dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      depthRef.current += 1;
      setIsDragging(true);
    },
    [enabled],
  );

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (!enabled || !dataTransferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
    },
    [enabled],
  );

  const onDragLeave = useCallback(
    (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) setIsDragging(false);
    },
    [enabled],
  );

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      e.stopPropagation();
      reset();
      const files = extractDataTransferFiles(e.dataTransfer);
      if (files.length > 0) onFilesRef.current(files);
    },
    [enabled, reset],
  );

  return {
    isDragging,
    dropHandlers: {
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
    },
  };
}
